import {
  useGlobalSearchParams,
  useLocalSearchParams
} from "expo-router";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { Platform, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import type { Dance } from "@recital-master/shared";
import { formatDuration, formatDurationLong, searchDances } from "@recital-master/shared";

import { AppHeader } from "@/components/AppHeader";
import { PinkChip } from "@/components/PinkChip";
import { DanceListHeader } from "@/components/DanceListHeader";
import { DanceRow } from "@/components/DanceRow";
import { RecitalHero } from "@/components/RecitalHero";
import { useAccount } from "@/data/AccountProvider";
import {
  useCatalog,
  useDancerIndex,
  useDances,
  usePerformance,
  usePerformanceDetailsStatus,
  usePerformanceFeaturedArtwork,
  useRecitalArtwork
} from "@/data/CatalogProvider";
import { Button } from "@/design/Button";
import { PlayFromStartIcon, PlayIcon } from "@/design/icons";
import { ContentSection, Divider, Screen } from "@/design/primitives";
import { Muted, Text } from "@/design/Text";
import { palette, space } from "@/design/tokens";
import { artGradient } from "@/lib/color";
import { useAppRouter } from "@/lib/appRouter";
import {
  compactEventDateLabel,
  displayTitle,
  DOT_SEPARATOR,
  joinWithDot
} from "@/lib/format";
import { dancersPath, recitalPath, useRecitalBySlug } from "@/lib/routes";
import { useAirPlayController } from "@/playback/airplay/AirPlayController";
import { useCastController } from "@/playback/cast/CastController";
import { getDanceListBottomInset } from "@/playback/cast/castMiniLayout";
import { myDancerPlayLabel } from "@/playback/myDancer";
import { useReadinessHold } from "@/providers/ReadinessGate";

export default function PerformanceScreen() {
  const {
    studioSlug,
    recitalSlug,
    performanceId,
    download_preview: downloadPreview
  } = useLocalSearchParams<{
    studioSlug: string;
    recitalSlug: string;
    performanceId: string;
    download_preview?: string;
  }>();
  const { castMini } = useGlobalSearchParams<{ castMini?: string }>();
  const router = useAppRouter();
  const insets = useSafeAreaInsets();
  const cast = useCastController();
  const airPlay = useAirPlayController();
  const performance = usePerformance(performanceId);
  const dances = useDances(performanceId);
  const dancerIndex = useDancerIndex(performanceId);
  const detailsStatus = usePerformanceDetailsStatus(performanceId);
  const { recitalResume, savedDancers, resume } = useAccount();
  const { status } = useCatalog();
  const routedRecital = useRecitalBySlug(studioSlug, recitalSlug);
  const recital =
    routedRecital?.recitalId === performance?.recitalId ? routedRecital : null;
  const recitalArtwork = useRecitalArtwork(recital?.recitalId);
  const performanceArtwork = usePerformanceFeaturedArtwork(performanceId);
  const [query, setQuery] = useState("");
  const miniPlayerVisible =
    Boolean(cast.nowPlaying ?? airPlay.nowPlaying) ||
    (__DEV__ &&
      Platform.OS === "web" &&
      (castMini === "1" || castMini === "fallback"));
  const danceListBottomInset = getDanceListBottomInset({
    miniPlayerVisible,
    safeAreaBottom: insets.bottom
  });
  const openWatch = useCallback(
    (path: string) => router.navigate(path as never),
    [router]
  );
  useReadinessHold(
    `performance-details-${performanceId ?? "unknown"}`,
    status === "ready" && (!performance || detailsStatus !== "loading")
  );

  const storedResumeState = performanceId ? resume[performanceId] : undefined;
  const resumeState = storedResumeState?.completed ? undefined : storedResumeState;
  const resumeDance = resumeState
    ? dances.find((dance) => dance.danceId === resumeState.danceId)
    : undefined;
  const watchedIds = useMemo(
    () =>
      new Set([
        ...(storedResumeState?.watchedDanceIds ?? []),
        ...(recital ? recitalResume[recital.recitalId]?.watchedDanceIds ?? [] : [])
      ]),
    [recital, recitalResume, storedResumeState]
  );
  const isWatched = (dance: Dance) =>
    watchedIds.has(dance.danceId) ||
    (__DEV__ && downloadPreview === "unlocked" && dance.danceNumber % 3 === 1);

  /** Saved dancers who actually appear in this recital. */
  const dancersHere = useMemo(() => {
    if (!dancerIndex) return [];
    const present = new Set(dancerIndex.dancers.map((d) => d.dancerId));
    return savedDancers.filter((d) => present.has(d.dancerId));
  }, [dancerIndex, savedDancers]);
  const savedDancerIds = useMemo(
    () => new Set(dancersHere.map((d) => d.dancerId)),
    [dancersHere]
  );

  const filtered = useMemo(() => searchDances(dances, query), [dances, query]);

  if (!performance || !recital) {
    if (status !== "ready") return null;
    return (
      <Screen header={<AppHeader back backHref={recital ? recitalPath(recital) : undefined} />}>
        <View style={styles.missing}>
          <Text variant="title">This recital isn’t in your library.</Text>
          <Muted>Everything you can watch lives in your recitals.</Muted>
          <Button
            label="Go to my recitals"
            variant="secondary"
            onPress={() => router.replace("/recitals")}
          />
        </View>
      </Screen>
    );
  }

  // Normal navigation prepares details before pushing this route. On a cold
  // deep link the root curtain holds until this same boundary is ready, so an
  // empty dance list is never presented as real content.
  if (detailsStatus === "loading") return null;
  if (detailsStatus === "error") {
    return (
      <Screen header={<AppHeader back backHref={recital ? recitalPath(recital) : undefined} />}>
        <View style={styles.missing}>
          <Text variant="title">We couldn’t load this recital.</Text>
          <Muted>Check your connection and try again.</Muted>
          <Button label="Back" variant="secondary" onPress={() => router.back()} />
        </View>
      </Screen>
    );
  }

  const playFrom = (dance: Dance) =>
    openWatch(`/watch/${performance.performanceId}?mode=from-dance&dance=${dance.danceId}`);

  const partsInOrder = [...performance.parts].sort((a, b) => a.order - b.order);
  const multiPart = partsInOrder.length > 1;
  const tint = artGradient(recital.artColors);

  /* Resume covers the rest of the performance's queue, so the button quotes the
     remaining run time of that queue — not of the one dance. */
  const resumeQueue =
    resumeState?.mode === "my-dancer"
      ? dances.filter((dance) => dance.dancers.some((d) => savedDancerIds.has(d.dancerId)))
      : dances;
  const resumeIndex = resumeDance
    ? resumeQueue.findIndex((dance) => dance.danceId === resumeDance.danceId)
    : -1;
  const resumeRemainingSeconds =
    resumeState && resumeIndex >= 0
      ? Math.max(
          0,
          resumeQueue
            .slice(resumeIndex)
            .reduce((sum, dance) => sum + dance.durationSeconds, 0) -
            resumeState.positionSeconds
        )
      : 0;

  const resumeDanceProgress =
    resumeState && resumeDance && resumeDance.durationSeconds > 0
      ? Math.min(1, Math.max(0, resumeState.positionSeconds / resumeDance.durationSeconds))
      : 0;
  const resumeDanceSecondsLeft =
    resumeState && resumeDance
      ? Math.max(0, resumeDance.durationSeconds - resumeState.positionSeconds)
      : 0;

  /* A hero is for pressing play: at most three compact actions. The resume
     row's thumbnail progress bar says where Resume picks up. Dancer
     management lives on home and the account page, not here. */
  const actionsBlock = (
    <>
      {resumeState && resumeDance ? (
        <Button
          label={
            resumeRemainingSeconds > 0
              ? joinWithDot(["Resume", `${formatDurationLong(resumeRemainingSeconds)} left`])
              : "Resume"
          }
          tint={tint}
          icon={<PlayIcon size={16} color={palette.inkOnAccent} />}
          onPress={() =>
            openWatch(
              `/watch/${performance.performanceId}?mode=${resumeState.mode}&dance=${resumeState.danceId}&at=${Math.floor(resumeState.positionSeconds)}`
            )
          }
        />
      ) : null}
      <Button
        label="Play from start"
        variant={resumeState ? "secondary" : "primary"}
        tint={tint}
        icon={
          <PlayFromStartIcon
            size={16}
            color={resumeState ? palette.text : palette.inkOnAccent}
          />
        }
        onPress={() => openWatch(`/watch/${performance.performanceId}?mode=full-performance`)}
      />
      {dancersHere.length > 0 ? (
        <PinkChip
          label={myDancerPlayLabel(dancersHere)}
          onPress={() => openWatch(`/watch/${performance.performanceId}?mode=my-dancer`)}
        />
      ) : (
        <PinkChip
          label="Find my dancer"
          onPress={() => router.push(dancersPath(recital) as never)}
        />
      )}
    </>
  );

  return (
    <Screen
      backdropUri={performanceArtwork ?? recitalArtwork}
      contentBottomInset={danceListBottomInset}
      header={<AppHeader back backHref={recital ? recitalPath(recital) : undefined} />}
      keyboardAware
      keyboardShouldPersistTaps="handled"
    >

      <RecitalHero
        artworkUri={recitalArtwork}
        fallbackSeed={recital.recitalId}
        artworkLabel={String(recital.year)}
        eyebrow={recital.title}
        title={performance.title}
        meta={`${compactEventDateLabel(performance.eventDate)}${DOT_SEPARATOR}${performance.danceCount} dances${DOT_SEPARATOR}${formatDurationLong(performance.totalDurationSeconds)}`}
        progress={
          resumeState && resumeDance
            ? {
                fraction: resumeDanceProgress,
                label: `${displayTitle(resumeDance.title)}${DOT_SEPARATOR}${formatDuration(resumeDanceSecondsLeft)} left`
              }
            : undefined
        }
      >
        {actionsBlock}
      </RecitalHero>

      {/* The dances — one continuous program, part breaks are whispers */}
      <ContentSection
        header={<DanceListHeader title="All dances" value={query} onChangeText={setQuery} />}
      >
        {query ? (
          <View>
            {filtered.length === 0 ? (
              <Muted variant="small" style={styles.noMatches}>
                No dances match your search.
              </Muted>
            ) : (
              <Muted variant="small" style={styles.resultCount}>
                {filtered.length} {filtered.length === 1 ? "dance" : "dances"}
              </Muted>
            )}
            {filtered.map((dance) => (
              <DanceRow
                key={dance.danceId}
                dance={dance}
                onPress={() => playFrom(dance)}
                watched={isWatched(dance)}
                resumePoint={resumeState?.danceId === dance.danceId}
                resumeRemainingSeconds={
                  resumeState?.danceId === dance.danceId
                    ? Math.max(0, dance.durationSeconds - resumeState.positionSeconds)
                    : undefined
                }
                highlightDancerIds={savedDancerIds}
              />
            ))}
          </View>
        ) : (
          <View>
            {partsInOrder.map((part, partIndex) => (
              <Fragment key={part.partId}>
                {multiPart && partIndex > 0 ? <Divider label={part.title} /> : null}
                {filtered
                  .filter((dance) => dance.partId === part.partId)
                  .map((dance) => (
                    <DanceRow
                      key={dance.danceId}
                      dance={dance}
                      onPress={() => playFrom(dance)}
                      watched={isWatched(dance)}
                      resumePoint={resumeState?.danceId === dance.danceId}
                      resumeRemainingSeconds={
                        resumeState?.danceId === dance.danceId
                          ? Math.max(0, dance.durationSeconds - resumeState.positionSeconds)
                          : undefined
                      }
                      highlightDancerIds={savedDancerIds}
                    />
                  ))}
              </Fragment>
            ))}
          </View>
        )}
      </ContentSection>
    </Screen>
  );
}

const styles = StyleSheet.create({
  resultCount: {
    marginBottom: space.sm,
    marginTop: space.sm
  },
  noMatches: {
    paddingVertical: space.lg,
    textAlign: "center"
  },
  missing: {
    alignItems: "flex-start",
    gap: space.lg,
    marginTop: space.xl
  }
});
